import {
    forwardRef,
    ForwardRefRenderFunction,
    PropsWithChildren,
    useEffect,
    useImperativeHandle,
    useRef
} from 'react';

import { AspectRatio, Box, Flex } from '@chakra-ui/react';
import { motion, useAnimation } from 'framer-motion';
import ReactVisibilitySensor from 'react-visibility-sensor';

const MotionAspectRatio = motion(AspectRatio);

type Props = PropsWithChildren & { adaptOnActions?: boolean; fixedOnScroll?: boolean };
export interface AdaptivePlayerLayoutHandle {
    scrollToMain: () => void;
    layoutEl: HTMLDivElement | null;
    mainEl: HTMLDivElement | null;
}

const AdaptivePlayerLayout: ForwardRefRenderFunction<AdaptivePlayerLayoutHandle, Props> = (
    { children, fixedOnScroll = false },
    forwardedRef
) => {
    const layoutRef = useRef<HTMLDivElement>(null);
    const mainRef = useRef<HTMLDivElement>(null);
    const asideRef = useRef<HTMLDivElement>(null);
    const mainAnimationControls = useAnimation();

    const scrollToMain = () => {
        mainRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        mainAnimationControls.start({ height: 'auto' });
    }, [mainAnimationControls]);

    useEffect(() => {
        if (!fixedOnScroll) {
            makeMainSticky(true);
            scrollToMain();
        }
    }, [fixedOnScroll]);

    const makeMainSticky = (visibile: boolean) => {
        if (mainRef.current && mainRef.current.firstElementChild) {
            const mainFirstChild = mainRef.current.firstElementChild as HTMLDivElement;
            const rect = mainRef.current.getBoundingClientRect();
            const originHeight = rect.height;
            const isMainVisible = rect.top >= 0 && rect.left >= 0;

            if (fixedOnScroll && (!visibile || !isMainVisible)) {
                mainFirstChild.style.height = `${originHeight}px`;
                mainFirstChild.style.position = 'fixed';
                mainFirstChild.style.zIndex = '9';
                mainFirstChild.style.top = `0px`;
            } else {
                mainFirstChild.style.height = `${originHeight}px`;
                mainFirstChild.style.position = 'absolute';
                mainFirstChild.style.zIndex = '1';
                mainFirstChild.style.height = `100%`;
                scrollToMain();
            }
        }
    };

    useImperativeHandle(forwardedRef, () => ({
        scrollToMain: scrollToMain,
        layoutEl: layoutRef.current,
        mainEl: mainRef.current
    }));

    return (
        <Flex ref={layoutRef} flexDir="column">
            <ReactVisibilitySensor
                active={fixedOnScroll}
                resizeCheck
                scrollThrottle={5}
                onChange={makeMainSticky}
                scrollCheck
            >
                <MotionAspectRatio
                    ref={mainRef}
                    overflow="hidden"
                    animate={mainAnimationControls}
                    ratio={16 / 9}
                    maxH="80vh"
                >
                    <Box transition="top .5s ease-out" onClick={() => scrollToMain()}>
                        {Array.isArray(children) ? children[0] : null}
                    </Box>
                </MotionAspectRatio>
            </ReactVisibilitySensor>
            <Box
                position="relative"
                zIndex="1"
                ref={asideRef}
                flex="1"
                bg="white"
                w="full"
                overflow="hidden"
            >
                {Array.isArray(children) ? children[1] : null}
            </Box>
        </Flex>
    );
};
export default forwardRef(AdaptivePlayerLayout);
